import { format } from 'date-fns';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { CalendarIcon, Clock, Bell, Repeat } from 'lucide-react';
import { cn } from '../../lib/utils';

export const AddInterestDialog = ({ open, onOpenChange, interest, onChange, onSubmit }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Add an Interest</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">What's on your mind?</Label>
            <Input
              id="title"
              placeholder="Enter your interest..."
              value={interest.title}
              onChange={(e) => onChange({ ...interest, title: e.target.value })}
              data-testid="interest-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="Add some details..."
              value={interest.description}
              onChange={(e) => onChange({ ...interest, description: e.target.value })}
              rows={2}
              data-testid="interest-description-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Initial Quadrant</Label>
            <RadioGroup
              value={interest.quadrant}
              onValueChange={(value) => onChange({ ...interest, quadrant: value })}
              className="grid grid-cols-2 gap-2"
            >
              <Label className="flex items-center gap-2 p-2 rounded-lg border border-emerald-200 dark:border-emerald-800 cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-950/30 [&:has(:checked)]:bg-emerald-100 dark:[&:has(:checked)]:bg-emerald-900/30 text-xs">
                <RadioGroupItem value="q1" />
                <span>High Attention / High Impact</span>
              </Label>
              <Label className="flex items-center gap-2 p-2 rounded-lg border border-amber-200 dark:border-amber-800 cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-950/30 [&:has(:checked)]:bg-amber-100 dark:[&:has(:checked)]:bg-amber-900/30 text-xs">
                <RadioGroupItem value="q2" />
                <span>High Attention / Low Impact</span>
              </Label>
              <Label className="flex items-center gap-2 p-2 rounded-lg border border-amber-200 dark:border-amber-800 cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-950/30 [&:has(:checked)]:bg-amber-100 dark:[&:has(:checked)]:bg-amber-900/30 text-xs">
                <RadioGroupItem value="q3" />
                <span>Low Attention / High Impact</span>
              </Label>
              <Label className="flex items-center gap-2 p-2 rounded-lg border border-rose-200 dark:border-rose-800 cursor-pointer hover:bg-rose-50 dark:hover:bg-rose-950/30 [&:has(:checked)]:bg-rose-100 dark:[&:has(:checked)]:bg-rose-900/30 text-xs">
                <RadioGroupItem value="q4" />
                <span>Low Attention / Low Impact</span>
              </Label>
            </RadioGroup>
          </div>
          
          <DeadlineSection interest={interest} onChange={onChange} prefix="" />
          <RepeatSection interest={interest} onChange={onChange} prefix="" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} data-testid="save-interest-btn">Add Interest</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DeadlineSection = ({ interest, onChange, prefix }) => (
  <div className="space-y-3 pt-2 border-t">
    <div className="flex items-center justify-between">
      <Label className="flex items-center gap-2">
        <CalendarIcon className="w-4 h-4" />
        {prefix === 'edit-' ? 'Deadline' : 'Set Deadline'}
      </Label>
      <Switch
        checked={interest?.hasDeadline || false}
        onCheckedChange={(checked) => onChange({ ...interest, hasDeadline: checked })}
        data-testid={`${prefix}deadline-toggle`}
      />
    </div>
    
    {interest?.hasDeadline && (
      <div className="space-y-3 pl-6">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Date</Label>
          <Popover modal={true}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !interest?.deadline && "text-muted-foreground"
                )}
                data-testid={`${prefix}deadline-date-picker`}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {interest?.deadline ? format(new Date(interest.deadline), "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={interest?.deadline ? new Date(interest.deadline) : undefined}
                onSelect={(date) => onChange({ ...interest, deadline: date })}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Time</Label>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <Input
              type="time"
              value={interest?.deadlineTime || '12:00'}
              onChange={(e) => onChange({ ...interest, deadlineTime: e.target.value })}
              className="w-32"
            />
          </div>
        </div>
        
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground flex items-center gap-1">
            <Bell className="w-3 h-3" />
            Reminder
          </Label>
          <Select
            value={interest?.reminder_minutes?.toString() || "none"}
            onValueChange={(val) => onChange({ 
              ...interest, 
              reminder_minutes: val === "none" ? null : parseInt(val) 
            })}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="No reminder" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No reminder</SelectItem>
              <SelectItem value="5">5 minutes before</SelectItem>
              <SelectItem value="15">15 minutes before</SelectItem>
              <SelectItem value="30">30 minutes before</SelectItem>
              <SelectItem value="60">1 hour before</SelectItem>
              <SelectItem value="1440">1 day before</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    )}
  </div>
);

export const RepeatSection = ({ interest, onChange, prefix }) => (
  <div className="space-y-2 pt-2 border-t">
    <Label className="flex items-center gap-2">
      <Repeat className="w-4 h-4" />
      Repeat
    </Label>
    <Select
      value={interest?.repeat || 'none'}
      onValueChange={(val) => onChange({ ...interest, repeat: val })}
    >
      <SelectTrigger className="w-full" data-testid={`${prefix}repeat-select`}>
        <SelectValue placeholder="No repeat" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">No repeat</SelectItem>
        <SelectItem value="daily">Daily</SelectItem>
        <SelectItem value="weekly">Weekly</SelectItem>
        <SelectItem value="monthly">Monthly</SelectItem>
        <SelectItem value="yearly">Yearly</SelectItem>
      </SelectContent>
    </Select>
  </div>
);
